import axios from 'axios';
import {
    emitAuthSessionLogout,
    emitAuthTokensRefreshed,
    getAuthToken,
    getCookiePath,
    getRefreshToken,
} from './authSession';

import { API_SERVER } from '../config/env';

const SERVER = API_SERVER;
const SERVER_BASE = typeof SERVER === 'string' ? SERVER.trim().replace(/\/+$/, '') : '';

const server = axios.create({
    baseURL: SERVER,
    timeout: 20000,
    timeoutErrorMessage: 'Servidor Instável. Tente mais tarde.',
});

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(token) {
    refreshSubscribers.forEach(({ resolve }) => resolve(token));
    refreshSubscribers = [];
}

function onRefreshFailed(error) {
    refreshSubscribers.forEach(({ reject }) => reject(error));
    refreshSubscribers = [];
}

function addRefreshSubscriber(resolve, reject) {
    refreshSubscribers.push({ resolve, reject });
}

server.interceptors.request.use(async config => {
    const accessToken = getAuthToken();
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
});

server.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest?._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    addRefreshSubscriber(
                        token => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(server(originalRequest));
                        },
                        err => reject(err),
                    );
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const cookiePath = getCookiePath();

            try {
                const refreshTokenDecoded = getRefreshToken();

                if (!refreshTokenDecoded) {
                    throw { response: { status: 401, data: { error: 'refresh_token_reject' } } };
                }

                const response = await axios.post(`${SERVER_BASE}/auth/refresh-token`, null, {
                    headers: {
                        refreshtoken: refreshTokenDecoded,
                    },
                });

                const newAccessToken = response.data.data.token;
                const newRefreshToken = response.data.data.refreshToken;

                emitAuthTokensRefreshed({
                    token: newAccessToken,
                    refreshtoken: newRefreshToken,
                    path: cookiePath,
                });

                server.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
                onRefreshed(newAccessToken);
                return server(originalRequest);
            } catch (err) {
                onRefreshFailed(err);

                if (err?.response?.data?.error === 'refresh_token_reject') {
                    emitAuthSessionLogout();
                }

                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    },
);

export default server;
